import { Col, Row, Table, Popconfirm } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as MoreActionsIcon } from "../../assets/more-actions.svg";
import CreateButton from "../../components/CreateButton";
import ErrorBanner from "../../components/ErrorBanner";
import Popup from "../../components/PopupMenu/Popup";
import SearchBar from "../../components/SearchBar";
import WaitIndicator from "../../components/WaitIndicator";
import { ScreenContext } from "../../contexts/ScreenContext";
import ProductService from "../../services/ProductService";
import RoleCheckService from "../../services/RoleCheckService";
import styles from "../Product/Products.module.css";

const TherapeuticEquivalencies = ({ user, refresh }) => {
  const { t } = useTranslation("therapeuticEquivalents");

  const { smallScreen } = useContext(ScreenContext);
  const smScreen = useMediaQuery(smallScreen);

  const location = useLocation();
  const navigate = useNavigate();

  const actionListItems = [
    {
      type: "edit",
      description: t("editButtonLabel"),
    },
    {
      type: "remove",
      description: t("removeButtonLabel"),
    },
  ];

  const [filterText, setFilterText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [locationPathname, setLocationPathname] = useState("");
  const [data, setData] = useState([]);
  const [showPopup, setShowPopup] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    setLocationPathname(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    async function getTherapeuticEquivalencies() {
      try {
        setLoading(true);
        let list = await ProductService.getTherapeuticEquivalencies(user);
        list = list.sort((a, b) => {
          if (a.teCode < b.teCode) {
            return -1;
          }
          if (b.teCode < a.teCode) {
            return 1;
          }
          return 0;
        });
        setData(list);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    }
    getTherapeuticEquivalencies();
  }, [user, refresh]);

  const handleSearch = (searchText) => {
    searchText = searchText.trim();
    setFilterText(searchText);
  };

  const handleCreate = () => {
    navigate("create");
  };

  const handleAction = (action, row) => {
    switch (action.type) {
      case "edit":
        navigate("create", { state: { teCode: row.teCode } });
        break;
      case "remove":
        setShowConfirmDelete(row);
        break;
      default:
        break;
    }
  };

  const removeTherapeuticEquivalent = (row) => {
    setDeleteLoading(true);
    ProductService.getTherapeuticEquivalentProducts(user, row.teCode)
      .then(products => {
        const updatePromises = products.map(product => {
          product.therapeuticEquivalencyCode = "";
          return ProductService.update(user, product);
        });
        return Promise.all(updatePromises);
      })
      .then(() => {
        const updatedData = data.filter(item => item.teCode !== row.teCode);
        setData(updatedData);
      })
      .finally(() => {
        setDeleteLoading(false);
        setShowConfirmDelete(null);
      });
  };

  if (loading) {
    return <WaitIndicator message={t("fetching")} />;
  } else {
    return (
      <>
        <Outlet />
        {error && (
          <div className={styles.Products__errorBannerContainer}>
            <ErrorBanner message={error} onClose={() => setError(null)} />
          </div>
        )}
        {(locationPathname === "/settings/therapeuticEquivalencies" ||
          locationPathname === "/inventory/substitutions") && (
          <Row>
            {!smScreen && <Col span={24} style={{ height: "70px" }}></Col>}
            <Col span={smScreen ? 24 : 12}>
              <SearchBar
                placeholder={t("searchPlaceholder")}
                onSearch={handleSearch}
                parentFilterText={filterText}
                focus={true}
              />
            </Col>
            <Col span={smScreen ? 24 : 12}>
              <Row justify="end">
                <Col>
                  {RoleCheckService.addEquivalencies(user) && (
                    <CreateButton
                      onClick={handleCreate}
                      isEnabled={true}
                      labelName={t("createButtonLabel")}
                    />
                  )}
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Table
                columns={[
                  {
                    title: t("col0"),
                    render: (row) => row.teCode,
                  },
                  {
                    title: t("col1"),
                    render: (row) => row.productCount,
                  },
                  {
                    title: t("col2"),
                    render: (row) => {
                      if (RoleCheckService.addEquivalencies(user)) {
                        return (
                          <li className={styles.Products__actionsContainer}>
                            <div
                              className={styles.Products__actionsIconContainer}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowPopup(row);
                              }}
                            >
                              <MoreActionsIcon />
                              {showPopup === row && (
                                <div
                                  className={styles.Products__popupContainer}
                                >
                                  <Popup
                                    menuItems={actionListItems}
                                    rightJustify={true}
                                    onClose={() => setShowPopup(null)}
                                    onAction={(action) => {
                                      handleAction(action, row);
                                      setShowPopup(null);
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                            {showConfirmDelete === row && (
                              <Popconfirm
                                okText={t("removeButtonLabel")}
                                cancelText={t("cancelButtonLabel")}
                                onConfirm={() => removeTherapeuticEquivalent(row)}
                                onCancel={() => setShowConfirmDelete(null)}
                                title={t("areYouSure")}
                                okButtonProps={{ 
                                  type: "link", 
                                  style: { color: "#d9534f" },
                                  loading: deleteLoading
                                }}
                                cancelButtonProps={{ disabled: deleteLoading }}
                                open={true}
                              >
                                {deleteLoading && (
                                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                                              display: 'flex', justifyContent: 'center', alignItems: 'center', 
                                              backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                                  </div>
                                )}
                              </Popconfirm>
                            )}
                          </li>
                        );
                      } else {
                        return "";
                      }
                    },
                    width: 120,
                  },
                ]}
                rowKey={(record) => record._id}
                dataSource={data.filter((o) =>
                  filterText
                    ? o.teCode.toUpperCase().includes(filterText.toUpperCase())
                    : true
                )}
                pagination={false}
                scroll={
                  !data ||
                  data.filter((o) =>
                    filterText
                      ? o.teCode
                          .toUpperCase()
                          .includes(filterText.toUpperCase())
                      : true
                  ).length === 0
                    ? undefined
                    : { y: window.innerHeight - 300 }
                }
              />
            </Col>
          </Row>
        )}
      </>
    );
  }
};

export default TherapeuticEquivalencies;
